import React, { useState, useEffect, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { ProjectModel, ProjectNote } from '../../../types/projects';
import { ProjectNoteModal } from '../modals/ProjectNoteModal';
import { ConfirmDeleteModal } from '../../../Components/ConfirmDeleteModal';
import { MetadataInfoButton } from '../../../Components/MetadataInfoButton';
import { useUserNames } from '../../../Helpers/useUserNames';

interface ProjectNotesSectionProps {
    project: ProjectModel;
    onAddNote: (title: string, content: string) => Promise<void>;
    onUpdateNote: (noteKey: string, title: string, content: string) => Promise<void>;
    onDeleteNote: (noteKey: string) => Promise<void>;
    submitting?: boolean;
    /**
     * When true, renders a read-only view: hides the add button and the
     * per-note edit/delete controls (and the add/edit/delete modals). Note
     * cards, titles, content, dates and the MetadataInfoButton stay visible.
     * Defaults to false so the Projects pages behave exactly as before.
     */
    readOnly?: boolean;
}

export const ProjectNotesSection: React.FC<ProjectNotesSectionProps> = ({
    project,
    onAddNote,
    onUpdateNote,
    onDeleteNote,
    submitting = false,
    readOnly = false,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [editingNote, setEditingNote] = useState<ProjectNote | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingDeleteNoteKey, setPendingDeleteNoteKey] = useState<string | null>(null);

    const { userNames, loadUserNames } = useUserNames();

    const notes = useMemo(
        () => project.notes ? Object.entries(project.notes) : [],
        [project.notes]
    );

    // Extract UIDs for user name loading
    const noteUids = useMemo(
        () => notes.flatMap(([, note]) => [note.createdBy, note.updatedBy].filter(Boolean) as string[]),
        [notes]
    );

    // Load user names for all notes
    useEffect(() => {
        if (noteUids.length > 0) {
            loadUserNames(noteUids);
        }
    }, [noteUids, loadUserNames]);

    const handleOpenAddModal = () => {
        setEditingNote(null);
        setShowModal(true);
    };

    const handleOpenEditModal = (note: ProjectNote) => {
        setEditingNote(note);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingNote(null);
    };

    const handleSave = async (title: string, content: string) => {
        if (editingNote) {
            await onUpdateNote(editingNote.key, title, content);
        } else {
            await onAddNote(title, content);
        }
    };

    const handleDeleteNoteClick = (noteKey: string) => {
        setPendingDeleteNoteKey(noteKey);
        setShowDeleteModal(true);
    };

    const handleConfirmDeleteNote = async () => {
        if (pendingDeleteNoteKey) {
            await onDeleteNote(pendingDeleteNoteKey);
            setPendingDeleteNoteKey(null);
        }
    };

    return (
        <div style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid #dee2e6'
        }}>
            <div style={{ border: "1px solid #dee2e6", borderRadius: "10px", backgroundColor: "#fff" }}>
                <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '12px 16px',
                    borderBottom: '1px solid #dee2e6',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h5 style={{ margin: 0 }}>Anteckningar</h5>
                        {!readOnly && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleOpenAddModal}
                                disabled={submitting}
                            >
                                <Plus size={14} className="me-1" />
                                Lägg till anteckning
                            </Button>
                        )}
                    </div>
                </div>
                <div style={{ padding: '24px' }}>
                    {notes.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {notes.map(([noteKey, note]) => (
                                <div key={noteKey} style={{ border: '1px solid #dee2e6', borderRadius: '10px', backgroundColor: '#fff' }}>
                                    <div style={{ padding: '16px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <div style={{ flex: 1 }}>
                                                <h6 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>
                                                    {note.title}
                                                </h6>
                                                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                                    {note.content}
                                                </p>
                                                <small style={{ color: '#666', marginTop: '20px', display: 'block' }}>
                                                    Skapad: {note.createdAt}
                                                    {note.updatedAt && ` • Uppdaterad: ${note.updatedAt}`}
                                                </small>
                                            </div>
                                            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                                <MetadataInfoButton
                                                    entries={[
                                                        {
                                                            label: 'Skapad',
                                                            date: note.createdAt,
                                                            userName: userNames[note.createdBy] || 'Okänd användare',
                                                        },
                                                        ...(note.updatedAt && note.updatedBy ? [{
                                                            label: 'Uppdaterad',
                                                            date: note.updatedAt,
                                                            userName: userNames[note.updatedBy] || 'Okänd användare',
                                                        }] : []),
                                                    ]}
                                                />
                                                {!readOnly && (
                                                    <>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleOpenEditModal(note)}
                                                            disabled={submitting}
                                                        >
                                                            <Edit2 size={14} />
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleDeleteNoteClick(noteKey)}
                                                            disabled={submitting}
                                                        >
                                                            <Trash2 size={14} />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p style={{ color: '#666', fontStyle: 'italic' }}>Inga anteckningar ännu</p>
                    )}
                </div>
            </div>

            {!readOnly && (
                <>
                    <ProjectNoteModal
                        show={showModal}
                        onHide={handleCloseModal}
                        onSave={handleSave}
                        existingNote={editingNote}
                        submitting={submitting}
                    />

                    <ConfirmDeleteModal
                        show={showDeleteModal}
                        onHide={() => {
                            setShowDeleteModal(false);
                            setPendingDeleteNoteKey(null);
                        }}
                        onConfirm={handleConfirmDeleteNote}
                        title="Radera anteckning"
                        message="Är du säker på att du vill radera denna anteckning? Detta kan inte ångras."
                    />
                </>
            )}
        </div>
    );
};
